import {defineAbilityConfig} from '../system/defineAbilityConfig';
import {WHEN_NOT_IN_OPTIMIZE_MODE} from './optimization';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

export const canAccessVariablesPanel = defineAbilityConfig([
  {
    abilities: {canDesign: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
    },
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: false,
    },
  },
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      webApps: true,
    },
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: false,
    },
  },
  {
    abilities: {canDesign: true, hasPermissionToAccessVariablesPanel: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      canvas: true,
      webApps: true,
    },
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
  {
    abilities: {hasPermissionToAccessVariablesPanel: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {
      webApps: true,
    },
    featureFlags: {
      [FEATURE_FLAGS.REALTIME]: true,
    },
  },
  {
    // Campaign Build Mode - allow variables panel in edit mode
    abilities: {isInCampaignBuildMode: true},
    siteModes: {edit: true},
    workflows: {canvas: true},
  },
] as const);

/**
 * This ability is used to determine whether or not the user can view the Variables Panel when
 * the user has the ability to access the Variables Panel or the Libraries Variables Panel.
 * It's used in the `WebflowColorPicker` to determine whether or not to direct them to open the
 * Variables Panel when they click on the color swatch that is a variable.
 */
export const canViewVariablesPanel = defineAbilityConfig([
  {
    abilities: {canDesign: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {canvas: true},
  },
  {abilities: {canAccessLibrariesVariablesPanel: true}},
  {
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
    siteModes: {design: true},
    workflows: {webApps: true},
  },
  {
    // Campaign Build Mode - allow viewing variables panel in edit mode
    abilities: {isInCampaignBuildMode: true},
    siteModes: {edit: true},
    workflows: {canvas: true},
  },
] as const);

/**
 * This ability is used to determine whether or not the user can access the Libraries Variables Panel.
 * The user must have the ability to access the Variables panel, have libraries with variables installed,
 * and have the feature flag for libraries enabled.
 */
export const canAccessLibrariesVariablesPanel = defineAbilityConfig({
  abilities: {canAccessVariablesPanel: true},
  features: {librariesWithVariables: true},
} as const);

// This is the ability to modify style variables
export const canModifyVariables = defineAbilityConfig({
  userTypes: {
    authorizedInDesigner: true,
    unauthorized: true,
  },
  siteModes: {design: true},
  workflows: {canvas: true, webApps: true},
  branches: {
    main: true,
  },
  permission: ['manage', 'siteVariable'],
  optimization: WHEN_NOT_IN_OPTIMIZE_MODE,
} as const);

/**
 * This ability is used to determine whether or not the user has permission to access the Variables Panel.
 * When Realtime is enabled, permission checks will be separate from hidden/visible checks since users may
 * not necessarily see the variables panel even if they have permission to access it.
 */
export const hasPermissionToAccessVariablesPanel = defineAbilityConfig([
  {
    abilities: {canAccessDesignSiteMode: true},
    userTypes: {
      authorizedInDesigner: true,
      unauthorized: true,
    },
  },
] as const);
